import { div, p, span, type ChildNode, type ElProps, type Mountable } from '@llui/dom';

/**
 * `<EmptyState>` — the one canonical empty placeholder (per `30-design-system.md §8`).
 *
 * Consolidates the ~10 bespoke "nothing here yet" blocks (empty entity
 * lists, empty draft inbox, empty search, empty reveals tab, …). A
 * centered icon + an optional title + a message + an optional action
 * slot (usually a `button(...)` from `@dl/ui`).
 *
 * UNREVIEWED (Phase A): minimal baseline; flagged in `/ui-gallery`.
 *
 * A11y: the icon is decorative (`aria-hidden`) — the message carries the
 * meaning. The block is a plain region, not a landmark; the caller
 * places it inside its own section/list context.
 */

export interface EmptyStateOptions {
  /** Optional heading (default sans) above the message. */
  title?: ChildNode;
  /** The explanatory line — required (the meaning lives here, not the icon). */
  message: ChildNode;
  /** Optional decorative leading icon (a Lucide glyph from the caller). */
  icon?: Mountable;
  /** Optional action slot — usually a primary/secondary `button(...)`. */
  action?: Mountable;
  /** Forwarded `data-testid`. */
  testid?: string;
  /** Arbitrary extra attributes (e.g. `data-root`) ride through here. */
  attrs?: ElProps;
}

export function emptyState(opts: EmptyStateOptions): Mountable {
  const children: Mountable[] = [];

  if (opts.icon !== undefined) {
    children.push(span({ class: 'dl-empty-state-icon', 'aria-hidden': 'true' }, [opts.icon]));
  }
  if (opts.title !== undefined) {
    children.push(p({ class: 'dl-empty-state-title' }, [opts.title]));
  }
  children.push(p({ class: 'dl-empty-state-message' }, [opts.message]));
  if (opts.action !== undefined) {
    children.push(div({ class: 'dl-empty-state-action' }, [opts.action]));
  }

  return div(
    {
      ...(opts.attrs ?? {}),
      class: 'dl-empty-state',
      ...(opts.testid !== undefined ? { 'data-testid': opts.testid } : {}),
    },
    children,
  );
}
